/**
 * Whether the user holds any paid access, mirroring the backend's `User::hasAnyAccess()`.
 *
 * Label scanning is gated on ANY paid access (diet / workout / AI), not on the AI plan alone —
 * `ScanLabelResolver` returns `AiAccessDenied` on the same condition. Keeping the two in step
 * matters: a front end that is stricter than the server hides a feature the user has paid for.
 *
 * All three keys are written by `UserDataConnectorApollo` after login, each from its own field on
 * `me`: `aiAccess.hasAccess`, `diet.accessTo` and `videoWorkout.accessTo`.
 *
 * In the native webview none of these keys are set — the app opens the webview straight on the
 * target URL, so no screen that writes them has mounted. `mobileAppMode` therefore bypasses this
 * check entirely at the call site.
 */
export const hasAnyPaidAccess = (): boolean => {
  try {
    return (
      sessionStorage.getItem('aiAccess') === 'true' ||
      sessionStorage.getItem('dietAccess') === 'true' ||
      sessionStorage.getItem('workoutAccess') === 'true'
    );
  } catch {
    // Private-mode Safari can throw on storage access; fall back to "no access" and let the
    // backend decide, rather than crashing the screen.
    return false;
  }
};

/**
 * Whether the user holds the AI plan specifically.
 *
 * Used where the entry point is advertised as an AI feature — the action sheet marks its scan
 * entries with the AI badge and a "get access" pill keyed on the AI plan, so the screen behind them
 * must require the same thing the label promises. The backend is deliberately more permissive
 * (`hasAnyAccess()`), which makes this a product-level restriction on that entry point, not a
 * security boundary: the same scan is reachable with any paid plan through the add-product form.
 */
export const hasAiAccess = (): boolean => {
  try {
    return sessionStorage.getItem('aiAccess') === 'true';
  } catch {
    return false;
  }
};

/**
 * Whether the user holds the diet plan OR the AI plan — the product rule for creating quick meals
 * (Confluence "Tracker - quick meal", section "Dostępy"). Workout-only access is not enough here,
 * unlike `hasAnyPaidAccess`.
 */
export const hasDietOrAiAccess = (): boolean => {
  try {
    return (
      sessionStorage.getItem('aiAccess') === 'true' ||
      sessionStorage.getItem('dietAccess') === 'true'
    );
  } catch {
    return false;
  }
};
